import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Award, Users, Globe, TrendingUp, Heart, Target, ArrowRight } from 'lucide-react';

export const AboutPage = () => {
  const stats = [
    { icon: Globe, value: '99.9%', label: 'Uptime Guarantee' },
    { icon: Award, value: '18+', label: 'Years Experience' },
    { icon: TrendingUp, value: 'Fast', label: 'LiteSpeed Servers' },
  ];

  const values = [
    {
      icon: Target,
      title: 'Customer First',
      description: 'Your success is our priority. We go above and beyond to ensure your satisfaction.',
    },
    {
      icon: Heart,
      title: 'Reliability',
      description: 'We build trust through consistent, dependable service and transparent communication.',
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'We strive for excellence in everything we do, from infrastructure to customer support.',
    },
    {
      icon: TrendingUp,
      title: 'Innovation',
      description: 'We continuously evolve our services to leverage the latest technology and best practices.',
    },
  ];

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="py-24 bg-foreground relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-white mb-6">
            About 961Host
          </h1>
          <p className="text-xl text-white/70 max-w-3xl mx-auto">
            Providing reliable web hosting solutions since 2007
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
            <p>
              Founded in 2007, <span className="font-semibold text-foreground">961Host</span> has been at the forefront of web hosting innovation, serving thousands of customers worldwide. What started as a small hosting company has grown into a trusted provider of premium hosting solutions.
            </p>
            <p>
              Our mission is simple: to provide fast, reliable, and affordable web hosting services backed by exceptional customer support. We believe that everyone deserves access to premium hosting features without breaking the bank.
            </p>
            <p>
              With reliable infrastructure and optimized servers, we ensure your website loads quickly and stays secure. Our infrastructure is built on enterprise-grade hardware with redundant systems to guarantee maximum uptime.
            </p>
            <p>
              Over the years, we've helped many businesses and individuals launch successful websites, from personal blogs to e-commerce platforms. Our team of dedicated professionals works around the clock to ensure your website stays online and performs at its best.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-secondary/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <Card key={idx} className="text-center card-premium">
                  <CardContent className="p-8 space-y-3">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10">
                      <Icon className="w-7 h-7 text-primary" />
                    </div>
                    <div className="text-4xl font-semibold text-foreground">{stat.value}</div>
                    <div className="text-muted-foreground">{stat.label}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-semibold text-foreground mb-4">
              Our Core Values
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {values.map((value, idx) => {
              const Icon = value.icon;
              return (
                <Card key={idx} className="card-premium">
                  <CardContent className="p-8 space-y-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">{value.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team CTA */}
      <section className="py-20 bg-foreground text-center">
        <div className="max-w-4xl mx-auto px-4">
          <Users className="w-16 h-16 text-white/80 mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl font-semibold text-white mb-4">
            Join Our Growing Community
          </h2>
          <p className="text-xl text-white/70 mb-8">
            Become part of the 961Host family today and experience hosting done right
          </p>
          <Button size="lg" variant="primary-light" asChild data-testid="about-get-started-btn">
            <a href="https://961host.net/order/index.php/store/web-hosting" target="_blank" rel="noopener noreferrer">
              Get Started Now
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
};
